const NS = 'MarketSummaryChartBlock';
const CLS = 'marketSummaryChartBlock';

import { ChartBlock } from '../../chartBlock/js/chartBlock';

let moment = require('moment');
let d3 = require('d3');

export class MarketSummaryChartBlock extends ChartBlock {
	constructor(element) {
		super(element, CLS);

		this.margin = {
			top: 30,
			right: 20,
			bottom: 30,
			left: 50
		};

		this.yTickValue = 100;
		this.dotRadius = 3;
		this.dotFocusRadius = 3;
	}

	setEmptyData() {
		// Placeholder data used, when there are no actual values…
		let placeholderData = [];
		let initialPlaceholderDataValue = 1200;
		let initialPlaceholderIncrementValue = 100;

		for(let i = 0; i < 12; i++) {
			placeholderData.push({
				value: initialPlaceholderDataValue + (i * initialPlaceholderIncrementValue),
				date: moment().month(i).day(1)
			});
		}

		this.emptyData = placeholderData;
	}

	drawChart() {
		this.clearSvg();
		this.setChartDimensions();

		if(this.$tooltip && this.$tooltip.length > 0) {
			this.$tooltip.remove(); // Removes the tooltip
		}

		this.drawSvg();
		this.drawTooltop();

		this.defineXAxesScales();
		this.defineYAxesScales();

		this.drawXAxes();
		this.drawYAxes();

		if(!this.isDataEmpty) {
			this.drawPaths();
			this.drawPoints();
		}
	}

	drawTooltop() {
		this.tooltip = this.getChartNode()
			.append('div')
			.attr('class', `${CLS}-tooltip`);

		this.$tooltip = this.$element.find(`.${CLS}-tooltip`);
	}

	defineXAxesScales() {
		this.xAxisBottom = d3.scaleTime()
			.range([0, this.width])
			.domain(d3.extent(this.data, (datum) => {
				return this.getMomentDate(datum.date).toDate();
			})).nice();

		this.xAxisTop = d3.scaleTime()
			.range([0, this.width])
			.domain(d3.extent(this.data, (datum) => {
				return this.getMomentDate(datum.date).toDate();
			})).nice();
	}

	defineYAxesScales() {
		this.yMaxTickValue = (Math.ceil((_.maxBy(this.data, (datum) => {
			return datum.value;
		}).value / this.yTickValue)) * this.yTickValue) + this.yTickValue;

		this.yMinTickValue = (Math.floor((_.minBy(this.data, (datum) => {
			return datum.value;
		}).value / this.yTickValue)) * this.yTickValue) - this.yTickValue;

		this.yAxisLeft = d3.scaleLinear()
			.range([this.height, 0])
			.domain([
				this.yMinTickValue,
				this.yMaxTickValue
			]);
	}

	drawXAxes() {
		this.svg.append('g')
			.attr('class', 'x-axis x-axis--bottom')
			.attr('transform', `translate(0,${this.height})`)
			.call(d3.axisBottom(this.xAxisBottom).tickFormat((date) => {
				return moment(date).format('MMMM').substr(0, 1);
			}).tickSize(-(this.height)));

		if(!this.isDataEmpty) {
			let yAxisOffset = 5; // Slight aesthetic margin tweak

			this.svg.append('g')
				.attr('class', 'x-axis x-axis--top')
				.attr('transform', `translate(0,${yAxisOffset})`)
				.call(d3.axisTop(this.xAxisTop).tickFormat((date, index, nodes) => {
					if(index === 0) {
						return moment(date).format('YYYY');
					}
					let previousIndex = d3.select(nodes[index - 1]);
					if((index > 0 && (moment(date).year() > moment(previousIndex.data()[0]).year()))) {
						// Translate the first elemement a bit to the left as the second element has a value
						if(index === 1) {
							d3.select(nodes[0]).attr('style', `transform: translate(-6px, -${yAxisOffset}px)`);
						}
						return moment(date).format('YYYY');
					}

					return '';
				}));
		}
	}

	drawYAxes() {
		this.svg.append('g')
			.attr('class', 'y-axis')
			.call(d3.axisLeft(this.yAxisLeft).tickFormat((value, index) => {
				if(this.mediaQuery.breakpointMaxWidth(this.mediaQuery.BREAKPOINTS.SM)) {
					if(index % 4 === 1) {
						return value;
					}
				}
				else {
					if(index % 2 === 1) {
						return value;
					}
				}
				return '';
			}).tickValues(d3.range(this.yMinTickValue, this.yMaxTickValue, this.yTickValue)));
	}

	// Create plot-line
	drawPaths() {
		this.svg.append('path')
			.datum(this.data)
			.attr('class', 'plot-line')
			.attr('d', d3.line()
				.x((datum) => {
					return this.xAxisBottom(this.getMomentDate(datum.date).toDate());
				})
				.y((datum) => {
					return this.yAxisLeft(datum.value);
				})
			);
	}

	// Create dots
	drawPoints() {
		this.svg.append('g')
			.selectAll('dot')
			.data(this.data)
			.enter()
			.append('circle')
			.attr('class', 'plot-circle')
			.attr('cx', (datum) => {
				return this.xAxisBottom(this.getMomentDate(datum.date).toDate());
			})
			.attr('cy', (datum) => {
				return this.yAxisLeft(datum.value);
			})
			.attr('r', this.dotRadius)
			.on('mouseover', (datum, index, nodes) => {
				this.tooltip.style('display', 'block');

				// Have the dots grow
				let circle = d3.select(nodes[index]);

				circle.attr('r', this.dotFocusRadius)
					.attr('cx', () => {
						return this.xAxisBottom(this.getMomentDate(datum.date).toDate());
					})
					.attr('cy', () => {
						return this.yAxisLeft(datum.value);
					});

				// Add active class to the related tick
				d3.select(nodes[index].parentNode.parentNode)
					.selectAll('.tick')
					.filter((innerData, innerIndex) => {
						return index === innerIndex;
					}).select('line').classed('active', true);
			})
			.on('mousemove', (datum, index, nodes) => {
				let month = this.getMomentDate(datum.date).format('MMM');
				let value = datum.value;

				this.tooltip.html(`<div class="${CLS}-tooltip-title">${month}</div><div class="${CLS}-tooltip-value">${value}</div>`);
				let tooltipWidth = this.$tooltip.outerWidth();
				// Subtract the left margin alongside half of the tooltip width. This horizontally centers the tooltip
				let leftPosition = (d3.mouse(nodes[index])[0] + this.margin.left) - (tooltipWidth / 2);
				let topPosition = (d3.mouse(nodes[index])[1]) - this.margin.top - 10;

				this.tooltip.style('left', `${leftPosition}px`)
					.style('top', `${topPosition}px`);
			})
			.on('mouseleave', (datum, index, nodes) => {
				this.tooltip.style('display', 'none');

				// Revert the dot size
				let circle = d3.select(nodes[index]);
				circle.attr('r', this.dotRadius)
					.attr('cx', () => {
						return this.xAxisBottom(this.getMomentDate(datum.date).toDate());
					})
					.attr('cy', () => {
						return this.yAxisLeft(datum.value);
					});

				// Revert active class to the related tick
				d3.select(nodes[index].parentNode.parentNode)
					.selectAll('.tick')
					.filter((innerDatum, innerIndex) => {
						return index === innerIndex;
					}).select('line').classed('active', false);
			});
	}
}
